import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, Plus, Search, X } from 'lucide-react';
import { Badge, Button, EmptyState, LoadingState, Pagination, SearchInput } from '@/components/ui';
import { ServiceCluster, ServiceClusterMember } from '@/api/service-clusters';
import { MedicalService } from '@/api/medicalServices';
import { useMedicalServices } from '@/hooks/useMedicalServices';
import { cn } from '@/utils/cn';
import { getClusterColor, getClusterIconGlyph } from './clusterVisuals';

interface ClusterServicesShellProps {
  isOpen: boolean;
  cluster: ServiceCluster | null;
  services: ServiceClusterMember[];
  onClose: () => void;
  onAddService: (clusterId: string, serviceId: string) => Promise<void>;
  onRemoveService: (clusterId: string, serviceId: string) => Promise<void>;
  onReorderServices: (clusterId: string, services: ServiceClusterMember[]) => Promise<void>;
}

const SERVICE_PICKER_PAGE_SIZE_OPTIONS = [50, 150, 250, 500] as const;
const DEFAULT_SERVICE_PICKER_PAGE_SIZE = 50;

function sortMembers(services: ServiceClusterMember[]) {
  return [...services].sort((a, b) => {
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return (a.service?.name || '').localeCompare(b.service?.name || '', 'ru');
  });
}

function serviceLabel(service?: MedicalService | ServiceClusterMember['service']) {
  return service?.name?.trim() || 'Без названия услуги';
}

export function ClusterServicesShell({
  isOpen,
  cluster,
  services,
  onClose,
  onAddService,
  onRemoveService,
  onReorderServices,
}: ClusterServicesShellProps) {
  const [serviceSearch, setServiceSearch] = useState('');
  const [servicePickerOffset, setServicePickerOffset] = useState(0);
  const [servicePickerLimit, setServicePickerLimit] = useState<number>(DEFAULT_SERVICE_PICKER_PAGE_SIZE);
  const [selectedMemberServiceIds, setSelectedMemberServiceIds] = useState<Set<string>>(new Set());
  const [selectedPickerServiceIds, setSelectedPickerServiceIds] = useState<Set<string>>(new Set());
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const pickerFilters = useMemo(
    () => ({
      search: serviceSearch.trim() || undefined,
      is_active: true,
    }),
    [serviceSearch]
  );
  const { services: pickerServices, total: pickerTotal, isLoading: pickerLoading } = useMedicalServices(
    pickerFilters,
    servicePickerOffset,
    servicePickerLimit
  );

  const sortedServices = useMemo(() => sortMembers(services), [services]);
  const memberServiceIds = useMemo(
    () => new Set(sortedServices.map((member) => member.service_id)),
    [sortedServices]
  );
  const selectablePickerServiceIds = useMemo(
    () => pickerServices.filter((service) => !memberServiceIds.has(service.id)).map((service) => service.id),
    [memberServiceIds, pickerServices]
  );
  const allMembersSelected = sortedServices.length > 0 && selectedMemberServiceIds.size === sortedServices.length;
  const allPickerPageSelected =
    selectablePickerServiceIds.length > 0 && selectablePickerServiceIds.every((serviceId) => selectedPickerServiceIds.has(serviceId));

  useEffect(() => {
    setServicePickerOffset(0);
    setSelectedPickerServiceIds(new Set());
  }, [serviceSearch, servicePickerLimit]);

  useEffect(() => {
    setSelectedMemberServiceIds(new Set());
    setSelectedPickerServiceIds(new Set());
  }, [cluster?.id]);

  useEffect(() => {
    setSelectedMemberServiceIds((current) => {
      const next = new Set([...current].filter((serviceId) => memberServiceIds.has(serviceId)));
      return next.size === current.size ? current : next;
    });
  }, [memberServiceIds]);

  useEffect(() => {
    if (pickerTotal > 0 && servicePickerOffset >= pickerTotal) {
      setServicePickerOffset(Math.max(0, Math.floor((pickerTotal - 1) / servicePickerLimit) * servicePickerLimit));
    }
  }, [pickerTotal, servicePickerLimit, servicePickerOffset]);

  if (!isOpen || !cluster) {
    return null;
  }

  const clusterColor = getClusterColor(cluster.color);
  const clusterIcon = getClusterIconGlyph(cluster.icon);

  const handleAdd = async (serviceId: string) => {
    if (memberServiceIds.has(serviceId)) {
      return;
    }
    setPendingAction(`add:${serviceId}`);
    try {
      await onAddService(cluster.id, serviceId);
    } finally {
      setPendingAction(null);
    }
  };

  const toggleMemberSelection = (serviceId: string) => {
    setSelectedMemberServiceIds((current) => {
      const next = new Set(current);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const togglePickerSelection = (serviceId: string) => {
    if (memberServiceIds.has(serviceId)) {
      return;
    }
    setSelectedPickerServiceIds((current) => {
      const next = new Set(current);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const handleToggleAllMembers = () => {
    setSelectedMemberServiceIds(allMembersSelected ? new Set() : new Set(sortedServices.map((member) => member.service_id)));
  };

  const handleTogglePickerPage = () => {
    setSelectedPickerServiceIds((current) => {
      const next = new Set(current);
      if (allPickerPageSelected) {
        selectablePickerServiceIds.forEach((serviceId) => next.delete(serviceId));
      } else {
        selectablePickerServiceIds.forEach((serviceId) => next.add(serviceId));
      }
      return next;
    });
  };

  const handleBulkRemove = async () => {
    const serviceIds = [...selectedMemberServiceIds];
    if (serviceIds.length === 0) {
      return;
    }
    if (!window.confirm(`Убрать из кластера ${serviceIds.length} услуг?`)) {
      return;
    }
    setPendingAction('bulk-remove');
    try {
      for (const serviceId of serviceIds) {
        await onRemoveService(cluster.id, serviceId);
      }
      setSelectedMemberServiceIds(new Set());
    } finally {
      setPendingAction(null);
    }
  };

  const handleBulkAdd = async () => {
    const serviceIds = [...selectedPickerServiceIds].filter((serviceId) => !memberServiceIds.has(serviceId));
    if (serviceIds.length === 0) {
      return;
    }
    setPendingAction('bulk-add');
    try {
      for (const serviceId of serviceIds) {
        await onAddService(cluster.id, serviceId);
      }
      setSelectedPickerServiceIds(new Set());
    } finally {
      setPendingAction(null);
    }
  };

  const handleRemove = async (serviceId: string) => {
    if (!window.confirm('Убрать услугу из кластера?')) {
      return;
    }
    setPendingAction(`remove:${serviceId}`);
    try {
      await onRemoveService(cluster.id, serviceId);
    } finally {
      setPendingAction(null);
    }
  };

  const handleMove = async (memberId: string, direction: -1 | 1) => {
    const currentIndex = sortedServices.findIndex((member) => member.id === memberId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= sortedServices.length) {
      return;
    }
    const nextServices = [...sortedServices];
    const [moved] = nextServices.splice(currentIndex, 1);
    nextServices.splice(nextIndex, 0, moved);

    setPendingAction(`reorder:${memberId}`);
    try {
      await onReorderServices(cluster.id, nextServices);
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div className="pointer-events-none fixed inset-0 z-50" role="dialog" aria-modal="false">
      <aside className="pointer-events-auto absolute left-1/2 top-16 isolate flex h-[min(720px,calc(100vh-6rem))] w-[min(1120px,calc(100vw-3rem))] -translate-x-1/2 flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.22)] ring-1 ring-black/10">
        <header className="border-b border-neutral-200 bg-white px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <span
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl"
                style={{ backgroundColor: `${clusterColor}18`, color: clusterColor }}
              >
                {clusterIcon}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-neutral-900">{cluster.name}</h2>
                  <Badge variant="secondary" className="text-xs">
                    {sortedServices.length} услуг
                  </Badge>
                </div>
                {cluster.description && <p className="mt-1 text-sm text-neutral-600">{cluster.description}</p>}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-9 w-9 p-0" title="Закрыть">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden bg-neutral-50 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)]">
          <section className="flex min-h-0 flex-col border-b border-neutral-200 bg-white p-5 lg:border-b-0 lg:border-r">
            <div className="mb-4 shrink-0 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Состав кластера</h3>
                <p className="mt-1 text-sm text-neutral-600">Читайте, меняйте порядок и убирайте лишние услуги.</p>
              </div>
            </div>
            {sortedServices.length > 0 && (
              <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2">
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    checked={allMembersSelected}
                    onChange={handleToggleAllMembers}
                    disabled={!!pendingAction}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  Выбрать все в кластере
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-500">Выбрано: {selectedMemberServiceIds.size}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={selectedMemberServiceIds.size === 0 || !!pendingAction}
                    onClick={() => setSelectedMemberServiceIds(new Set())}
                  >
                    Снять
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={selectedMemberServiceIds.size === 0 || !!pendingAction}
                    onClick={handleBulkRemove}
                  >
                    {pendingAction === 'bulk-remove' && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    Убрать выбранные
                  </Button>
                </div>
              </div>
            )}

            {sortedServices.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-neutral-300 p-6">
                <EmptyState type="default" title="В кластере пока нет услуг" description="Добавьте услуги справа или перетащите их из каталога." />
              </div>
            ) : (
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                {sortedServices.map((member, index) => {
                  const removePending = pendingAction === `remove:${member.service_id}`;
                  const reorderPending = pendingAction === `reorder:${member.id}`;
                  return (
                    <article
                      key={member.id}
                      className="rounded-xl border border-neutral-200 bg-neutral-50 p-3"
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedMemberServiceIds.has(member.service_id)}
                          onChange={() => toggleMemberSelection(member.service_id)}
                          disabled={!!pendingAction}
                          className="mt-2 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                          aria-label={`Выбрать услугу ${serviceLabel(member.service)}`}
                        />
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={index === 0 || !!pendingAction}
                            onClick={() => handleMove(member.id, -1)}
                            title="Поднять выше"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={index === sortedServices.length - 1 || !!pendingAction}
                            onClick={() => handleMove(member.id, 1)}
                            title="Опустить ниже"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-neutral-900">{serviceLabel(member.service)}</div>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500">
                            <span>ID: {member.service_id}</span>
                            {member.service?.external_id && <span>Код: {member.service.external_id}</span>}
                            {member.membership_source && <span>Источник: {member.membership_source}</span>}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!!pendingAction}
                          onClick={() => handleRemove(member.service_id)}
                          className="h-8 w-8 p-0 text-error hover:text-red-700"
                          title="Убрать из кластера"
                        >
                          {removePending || reorderPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                        </Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section className="flex min-h-0 flex-col bg-white p-5">
            <div className="mb-4 shrink-0 space-y-3">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Добавить из списка услуг</h3>
                  <p className="mt-1 text-sm text-neutral-600">Серверный поиск и пагинация по активным услугам текущей клиники.</p>
                </div>
                <label className="shrink-0 text-xs font-medium text-neutral-500">
                  Показывать
                  <select
                    value={servicePickerLimit}
                    onChange={(event) => setServicePickerLimit(Number(event.target.value))}
                    className="ml-2 rounded-lg border border-shell-border bg-white px-2 py-1 text-sm text-neutral-800 outline-none focus:border-primary-400"
                  >
                    {SERVICE_PICKER_PAGE_SIZE_OPTIONS.map((limit) => (
                      <option key={limit} value={limit}>
                        {limit}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <SearchInput value={serviceSearch} onChange={setServiceSearch} placeholder="Найти услугу для добавления..." />
            </div>
            {pickerServices.length > 0 && (
              <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2">
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    checked={allPickerPageSelected}
                    onChange={handleTogglePickerPage}
                    disabled={selectablePickerServiceIds.length === 0 || !!pendingAction}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  Выбрать страницу
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-500">Выбрано: {selectedPickerServiceIds.size}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={selectedPickerServiceIds.size === 0 || !!pendingAction}
                    onClick={() => setSelectedPickerServiceIds(new Set())}
                  >
                    Снять
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={selectedPickerServiceIds.size === 0 || !!pendingAction}
                    onClick={handleBulkAdd}
                  >
                    {pendingAction === 'bulk-add' && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    Добавить выбранные
                  </Button>
                </div>
              </div>
            )}

            {pickerLoading ? (
              <LoadingState />
            ) : pickerServices.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-neutral-300 p-6 text-center">
                <Search className="mx-auto h-8 w-8 text-neutral-300" />
                <p className="mt-2 text-sm font-medium text-neutral-700">Услуги не найдены</p>
                <p className="mt-1 text-sm text-neutral-500">Измените поиск или проверьте синхронизацию услуг.</p>
              </div>
            ) : (
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                {pickerServices.map((service) => {
                  const alreadyInCluster = memberServiceIds.has(service.id);
                  const addPending = pendingAction === `add:${service.id}`;
                  return (
                    <article
                      key={service.id}
                      className={cn(
                        'rounded-xl border p-3 transition-colors',
                        alreadyInCluster ? 'border-emerald-200 bg-emerald-50/60' : 'border-neutral-200 bg-white hover:border-primary-200'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedPickerServiceIds.has(service.id)}
                          onChange={() => togglePickerSelection(service.id)}
                          disabled={alreadyInCluster || !!pendingAction}
                          className="mt-2 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={`Выбрать услугу ${serviceLabel(service)}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-neutral-900">{serviceLabel(service)}</div>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500">
                            <span>ID: {service.id}</span>
                            {service.external_id && <span>Код: {service.external_id}</span>}
                          </div>
                        </div>
                        <Button
                          variant={alreadyInCluster ? 'secondary' : 'primary'}
                          size="sm"
                          disabled={alreadyInCluster || !!pendingAction}
                          onClick={() => handleAdd(service.id)}
                        >
                          {addPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
                          {alreadyInCluster ? 'В кластере' : 'Добавить'}
                        </Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {pickerTotal > 0 && (
              <Pagination
                offset={servicePickerOffset}
                limit={servicePickerLimit}
                total={pickerTotal}
                onOffsetChange={setServicePickerOffset}
                showPageNumbers
                maxPageNumbers={5}
                size="sm"
                className="mt-4 shrink-0 border-t border-shell-border pt-3"
                disabled={pickerLoading || !!pendingAction}
                testId="cluster-service-picker-pagination"
              />
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
